import React, { useState } from 'react';
import Layout from 'layouts/manager/Layout';
import Pagination from 'components/Pagination';

const tableData = [
  {
    name: '東京本店',
    email: 'tokyo@example.com',
    company: '株式会社A',
    address: '東京都千代田区1-1-1',
    tel: '03-1234-5678',
    mobile: '090-1111-2222',
    status: 'active',
  },
  {
    name: '大阪支店',
    email: 'osaka@example.com',
    company: '株式会社B',
    address: '大阪府大阪市北区2-2-2',
    tel: '06-2345-6789',
    mobile: '080-3333-4444',
    status: 'inactive',
  },
  {
    name: '名古屋営業所',
    email: 'nagoya@example.com',
    company: '株式会社C',
    address: '愛知県名古屋市中区3-3-3',
    tel: '052-345-6789',
    mobile: '070-5555-6666',
    status: 'active',
  },
];

type TableRow = {
  name: string;
  email: string;
  company: string;
  address: string;
  tel: string;
  mobile: string;
  status: string;
};

const actionLinks = [
  { label: '編集', color: 'text-green-700', hover: 'hover:underline', url: (row: TableRow, idx: number) => `/manager/supporter/edit/${idx + 1}` },
  { label: '削除', color: 'text-red-600', hover: 'hover:underline', url: (row: TableRow, idx: number) => `/manager/supporter/delete/${idx + 1}` },
  { label: '印刷', color: 'text-gray-700', hover: 'hover:underline', url: (row: TableRow, idx: number) => `/manager/supporter/print/${idx + 1}` },
];

type SortKey = 'name' | 'email' | 'company' | 'address' | 'tel' | 'mobile' | 'status';
type SortOrder = 'asc' | 'desc';

const Search: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [checked, setChecked] = useState<number[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // 検索フォーム用の初期値
  const [search, setSearch] = useState({
    name: '',
    email: '',
    company: '',
    tel: '',
    mobile: '',
    report: '',
    status: '',
  });

  // ページネーション用
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const totalRows = tableData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const pagedData = [...tableData].sort((a, b) => {
    const aValue = a[sortKey];
    const bValue = b[sortKey];
    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  }).slice((page - 1) * rowsPerPage, page * rowsPerPage);

  // チェックボックス
  const handleCheck = (idx: number) => {
    setChecked((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };
  const handleCheckAll = () => {
    if (checked.length === tableData.length) {
      setChecked([]);
    } else {
      setChecked(tableData.map((_, idx) => idx));
    }
  };

  // ソートリンク
  const renderSortLink = (key: SortKey, label: string) => (
    <button
      type="button"
      className="flex items-center text-xs font-medium text-gray-700 hover:underline focus:outline-none"
      onClick={() => {
        if (sortKey === key) {
          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
          setSortKey(key);
          setSortOrder('asc');
        }
      }}
    >
      {label}
      {sortKey === key && (
        <span className="ml-1 text-xs">{sortOrder === 'asc' ? '▲' : '▼'}</span>
      )}
    </button>
  );

  return (
    <Layout>
      <div className="mx-auto">
        {/* タイトル */}
        <h1 className="mb-3 text-lg font-bold text-gray-800">協力パートナー管理</h1>

        {/* 検索項目（開閉式） */}
        <div className="mb-6">
          <button
            className="flex items-center w-full px-4 py-2 rounded-t-lg"
            style={{ backgroundColor: '#f7fafd' }}
            onClick={() => setOpen((v) => !v)}
            type="button"
          >
            <span
              className="mr-2 text-lg transition-transform"
              style={{
                color: '#002c6a',
                transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
              }}
            >
              ▼
            </span>
            <span
              className="flex-1 text-base font-bold text-left"
              style={{ color: '#002c6a' }}
            >
              検索
            </span>
          </button>
          {open && (
            <div className="p-4 bg-white border rounded-b-lg shadow">
              <form className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label className="block mb-1 text-sm text-gray-600">名前</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" placeholder="名前を入力" value={search.name} onChange={e => setSearch({ ...search, name: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">メールアドレス(ID)</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" placeholder="メールアドレス(ID)を入力" value={search.email} onChange={e => setSearch({ ...search, email: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">会社名</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" placeholder="会社名を入力" value={search.company} onChange={e => setSearch({ ...search, company: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">電話番号</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" placeholder="電話番号を入力" value={search.tel} onChange={e => setSearch({ ...search, tel: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">携帯電話番号</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" placeholder="携帯電話番号を入力" value={search.mobile} onChange={e => setSearch({ ...search, mobile: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">登録レポート</label>
                  <select className="w-full px-2 py-1 border rounded" value={search.report} onChange={e => setSearch({ ...search, report: e.target.value })}>
                    <option value="">全て</option>
                    <option value="1">有</option>
                    <option value="0">無</option>
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">ステータス</label>
                  <select className="w-full px-2 py-1 border rounded" value={search.status} onChange={e => setSearch({ ...search, status: e.target.value })}>
                    <option value="">全て</option>
                    <option value="active">有効</option>
                    <option value="inactive">無効</option>
                  </select>
                </div>
                <div className="flex justify-start mt-2 md:col-span-3">
                  <button
                    type="submit"
                    className="px-3 py-1 text-sm transition rounded"
                    style={{ color: '#ffffff', background: '#002c6a' }}
                  >
                    検索
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* 一覧テーブル（枠で囲み、上部にボタン） */}
        <div className="p-4 bg-white border rounded shadow">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div className="flex gap-2">
              <button
                className="px-3 py-1 text-xs font-semibold rounded"
                style={{ background: '#002c6a', color: '#fff' }}
                type="button"
                onClick={() => { window.location.href = '/manager/supporter/add'; }}
              >
                協力パートナー追加
              </button>
              <button
                className="px-3 py-1 text-xs font-semibold border border-blue-900 rounded"
                style={{ color: '#002c6a', background: '#f7fafd' }}
                type="button"
              >
                CSVインポート
              </button>
              <button
                className="px-3 py-1 text-xs font-semibold border border-blue-900 rounded"
                style={{ color: '#002c6a', background: '#f7fafd' }}
                type="button"
              >
                CSVエクスポート
              </button>
            </div>
          </div>
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-8 px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                  <input
                    type="checkbox"
                    checked={checked.length === tableData.length}
                    onChange={handleCheckAll}
                    className="accent-blue-700"
                  />
                </th>
                <th className="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">{renderSortLink('name', '名前')}</th>
                <th className="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">{renderSortLink('email', 'メールアドレス')}</th>
                <th className="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">{renderSortLink('company', '会社名')}</th>
                <th className="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">{renderSortLink('address', '住所')}</th>
                <th className="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">{renderSortLink('tel', '電話番号')}</th>
                <th className="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">{renderSortLink('mobile', '携帯電話番号')}</th>
                <th className="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">{renderSortLink('status', 'ステータス')}</th>
                <th className="w-32 px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">操作</th>
              </tr>
            </thead>
            <tbody>
              {pagedData.map((row, idx) => (
                <tr key={row.name} className={idx % 2 === 0 ? 'bg-gray-50' : ''}>
                  <td className="px-2 py-2">
                    <input
                      type="checkbox"
                      checked={checked.includes(idx)}
                      onChange={() => handleCheck(idx)}
                      className="accent-blue-700"
                    />
                  </td>
                  <td className="px-4 py-2">{row.name}</td>
                  <td className="px-4 py-2">{row.email}</td>
                  <td className="px-4 py-2">{row.company}</td>
                  <td className="px-4 py-2">{row.address}</td>
                  <td className="px-4 py-2">{row.tel}</td>
                  <td className="px-4 py-2">{row.mobile}</td>
                  <td className="px-4 py-2">{row.status === 'active' ? '有効' : '無効'}</td>
                  <td className="flex flex-wrap w-32 gap-1 px-2 py-1 whitespace-normal">
                    {actionLinks.filter(action => action.label).map((action) => (
                      <a
                        key={action.label}
                        href={action.url(row, idx)}
                        className={`text-[12px] ${action.color} ${action.hover} px-1 py-0.5 rounded border border-transparent hover:bg-gray-100 transition`}
                      >
                        {action.label}
                      </a>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* ページネーション */}
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      </div>
    </Layout>
  );
};

export default Search;
